import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';

import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/layout/Navbar';
import './styles/globals.css';

// Lazy-loaded pages for code splitting
const HomePage        = lazy(() => import('./pages/HomePage'));
const LoginPage       = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage    = lazy(() => import('./pages/auth/RegisterPage'));
const FeedPage        = lazy(() => import('./pages/FeedPage'));
const ProfilePage     = lazy(() => import('./pages/ProfilePage'));
const DirectoryPage   = lazy(() => import('./pages/DirectoryPage'));
const CompanyPage     = lazy(() => import('./pages/CompanyPage'));
const MarketFinderPage = lazy(() => import('./pages/MarketFinderPage'));
const JobsPage        = lazy(() => import('./pages/JobsPage'));
const GroupsPage      = lazy(() => import('./pages/GroupsPage'));
const EventsPage      = lazy(() => import('./pages/EventsPage'));
const EducationPage   = lazy(() => import('./pages/EducationPage'));
const AdvertisePage   = lazy(() => import('./pages/AdvertisePage'));
const SearchPage      = lazy(() => import('./pages/SearchPage'));
const MessagesPage    = lazy(() => import('./pages/MessagesPage'));
const SettingsPage    = lazy(() => import('./pages/SettingsPage'));
const NotFoundPage    = lazy(() => import('./pages/NotFoundPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 2, // 2 min
    },
  },
});

// Route guards
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user) return <Navigate to="/feed" replace />;
  return children;
}

function PageLoader() {
  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--gray-50)'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 48, height: 48,
          background: 'var(--teal-500)',
          borderRadius: 'var(--radius-md)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px',
          fontSize: 20, fontWeight: 700, color: 'white'
        }}>EI</div>
        <div style={{ color: 'var(--gray-500)', fontSize: 14 }}>Loading…</div>
      </div>
    </div>
  );
}

function AppLayout({ children }) {
  return (
    <>
      <Navbar />
      <main style={{ paddingTop: 'var(--nav-height)', minHeight: '100vh' }}>
        {children}
      </main>
    </>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={
          <AppLayout><HomePage /></AppLayout>
        } />
        <Route path="/login" element={
          <PublicOnlyRoute>
            <AppLayout><LoginPage /></AppLayout>
          </PublicOnlyRoute>
        } />
        <Route path="/register" element={
          <PublicOnlyRoute>
            <AppLayout><RegisterPage /></AppLayout>
          </PublicOnlyRoute>
        } />

        {/* Protected routes */}
        <Route path="/feed" element={
          <PrivateRoute>
            <AppLayout><FeedPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/profile/:userId" element={
          <PrivateRoute>
            <AppLayout><ProfilePage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/directory" element={
          <PrivateRoute>
            <AppLayout><DirectoryPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/company/:companyId" element={
          <PrivateRoute>
            <AppLayout><CompanyPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/market-finder" element={
          <PrivateRoute>
            <AppLayout><MarketFinderPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/jobs" element={
          <PrivateRoute>
            <AppLayout><JobsPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/groups" element={
          <PrivateRoute>
            <AppLayout><GroupsPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/events" element={
          <PrivateRoute>
            <AppLayout><EventsPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/education" element={
          <PrivateRoute>
            <AppLayout><EducationPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/advertise" element={
          <PrivateRoute>
            <AppLayout><AdvertisePage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/search" element={
          <PrivateRoute>
            <AppLayout><SearchPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/messages" element={
          <PrivateRoute>
            <AppLayout><MessagesPage /></AppLayout>
          </PrivateRoute>
        } />
        <Route path="/settings" element={
          <PrivateRoute>
            <AppLayout><SettingsPage /></AppLayout>
          </PrivateRoute>
        } />

        {/* 404 */}
        <Route path="*" element={<AppLayout><NotFoundPage /></AppLayout>} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <ToastContainer
            position="bottom-right"
            autoClose={4000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme="colored"
          />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
